// Libraries
import { effect, EventEmitter, Injector, runInInjectionContext, Signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { addDays, addSeconds, differenceInDays, endOfDay, format, isAfter, isBefore, isWithinInterval, startOfDay, subSeconds } from 'date-fns';
import { Extension } from 'ng2-adsk-forge-viewer';
import { BehaviorSubject, Observable } from 'rxjs';

// Services
import { MetricService } from 'src/app/services/api/metrics/metric.service';
import { PlanningService } from 'src/app/services/api/plannings/planning.service';
import { TrackService } from 'src/app/services/api/tracks/track.service';
import { PlanningStore } from 'src/app/services/stores/planning.store';

// Interfaces
import { Fragment, PutFragment } from 'src/app/interfaces/api/fragment';
import { Metric } from 'src/app/interfaces/api/metric';
import { ExtendedModel } from 'src/app/interfaces/api/model';
import { PlanningTask } from 'src/app/interfaces/api/task';
import { Zone } from 'src/app/interfaces/api/zone';
import { AppColors, Config } from 'src/app/interfaces/shared/config';
import { PlanningDay, PlanningDisplayModel } from 'src/app/interfaces/shared/planning-display';
import { XYZ } from 'src/app/interfaces/shared/xyz';
import { ViewerExtensionParams } from 'src/app/interfaces/viewer/viewer-extension-params';

// Classes
import { lowerBound } from 'src/app/classes/shared/dev.tools';
import * as Tools from 'src/app/classes/shared/threeDTools';
import { Cursor } from 'src/app/classes/viewer/Cursor';
import { ForgeTools } from 'src/app/classes/viewer/forge-tools';
import { MetricExtension } from 'src/app/classes/viewer/metrics/extension';
import { PlanningDockingPanel } from 'src/app/classes/viewer/planning-model/main.docking-panel';
import { MetricShape } from 'src/app/classes/viewer/shapes/metric';
import { ZoneShape } from 'src/app/classes/viewer/shapes/zone';
import ViewerMaterials from '../viewer-materials';

export class PlanningExtension extends Extension {
    // Services
    translate: TranslateService = {} as TranslateService;
    planningService: PlanningService = {} as PlanningService;
    planningStore: PlanningStore = {} as PlanningStore;
    metricService: MetricService = {} as MetricService;
    trackService: TrackService = {} as TrackService;
    injector!: Injector;

    // Components API
    public onTaskUpdate: EventEmitter<any> = new EventEmitter<any>();
    public onTaskSelected: EventEmitter<any> = new EventEmitter<any>();

    // Variables
    public mainPanel = null;
    public subToolbar: Autodesk.Viewing.UI.ToolBar | null = null;
    public zones: Record<string, Zone> = {};
    public model: ExtendedModel | null = null;
    public toolName = "Autodesk.ADN.Viewing.Tool.PlanningExtension";

    currentTask: Signal<PlanningTask | null> = {} as Signal<PlanningTask | null>;
    selectedFragIds: string[] = [];
    fragIds: number[] = [];
    currentFragments: Fragment[] = [];
    selectedTasks: BehaviorSubject<PlanningTask[]> = new BehaviorSubject<PlanningTask[]>([]);
    selectedFragments: Fragment[] = [];
    self = this;
    declare viewer: Autodesk.Viewing.GuiViewer3D;
    gantt: Autodesk.Viewing.InstanceTree | null = null;
    instanceTree: Autodesk.Viewing.InstanceTree | null = null;
    fragments: Fragment[] = [];

    public dbFragments: Fragment[] = [];
    public onPlanningReady: any;
    public linkFragsToTask: Function = () => { };
    public onImageClick: any;
    public onLinkMetric: any;
    public taskName: string = '';
    public task!: PlanningTask;
    public startDate: string = '';
    public endDate: string = '';
    public currentDate: string | null = null;
    public metricExt: MetricExtension = {} as MetricExtension;

    uploadingFragments: boolean = false;
    rebuildFragments: boolean = false;
    scaleDivider: number = 25;
    is2d: boolean = false;
    fragmentObjects: THREE.Object3D[] = [];
    zoneMeshes: any = {};
    realtime = true;

    displayModel: PlanningDisplayModel = {
        done: true,
        todo: true,
        progress: true,
        late: true,
        ahead: true,
        unused: true,
    }

    public ganttManager: any;
    public planning: string = ''; //planning id
    fragIndex: Record<string, number> = {}; //fragid:index in array
    fragNameIndex: Record<string, number[]> = {}; //fragName:dbIds array
    taskIndex: Record<string, number> = {}; //taskId:index in array
    metrics: Metric[] = [];
    craneCycles: MetricShape[] = [];
    concretings: any[] = [];
    concretingPhases: Metric[] = [];
    colors: AppColors = Config.colors;
    viewerColors!: any;
    cursor: Cursor = {} as Cursor;

    private selectFragSub?: any;
    private planningSub?: any;
    private planningServiceSub?: any;
    private planningAddFragSub?: any;
    // model:string; //model id
    days: PlanningDay[] = [];
    planningButton!: any;

    todoMaterial = ViewerMaterials.todoMaterial
    doneMaterial = ViewerMaterials.doneMaterial
    progressMaterial = ViewerMaterials.progressMaterial
    lateMaterial = ViewerMaterials.lateMaterial
    aheadMaterial = ViewerMaterials.aheadMaterial
    unusedMaterial = ViewerMaterials.unusedMaterial
    fragmentObjectMaterial = ViewerMaterials.fragmentObjectMaterial

    forgeTools!: ForgeTools;

    // ---------------- Getter/Setter ---------------- //

    get tasks(): Signal<PlanningTask[]> {
        return this.planningStore.tasks;
    }

    // ---------------- Functions ---------------- //

    private toolbarCreatedHandler = () => {
        this.createUI();
        // this.loadTaskSelected();
    };

    hexToVector4(hex: string, alpha = 1): THREE.Vector4 {
        const color = new THREE.Color(hex);
        return new THREE.Vector4(color.r, color.g, color.b, alpha);
    }

    public async init(params: ViewerExtensionParams) {
        Object.assign(this, params);
        this.forgeTools = new ForgeTools(this.viewer);

        this.viewerColors = {
            todo: this.hexToVector4(this.colors.warning),
            done: this.hexToVector4(this.colors.success),
            progress: this.hexToVector4(this.colors.primary),
            late: this.hexToVector4(this.colors.danger),
            ahead: this.hexToVector4(this.colors.success),
            unused: this.hexToVector4(this.colors.info),
            fragmentObject: this.hexToVector4(this.colors.dark)
        }

        if (params.planningStore)
            this.planningStore = params.planningStore

        this.currentTask = this.planningStore.currentTask;

        this.injector = params.injector;
        this.selectedFragIds = [];

        runInInjectionContext(this.injector, () => {
            // ⏳ Attendre chargement
            effect(() => {
                const isLoadingMetrics = this.planningStore.isLoadingMetrics()
                const isLoadingTasks = this.planningStore.isLoadingTasks()
                const isLoadingFragments = this.planningStore.isLoadingFragments()

                if (!isLoadingMetrics && !isLoadingTasks && !isLoadingFragments) {
                    this.setFragIndex();
                }
            })

            // 🔄 Fragments actualisés
            effect(() => {
                this.fragments = this.planningStore.fragments();

                this.setFragColorAtDate(this.getFragAtDate(), false); // Redessiner
            });

            // 🎯 Tâche isolée
            effect(() => {
                const currentTask = this.currentTask();

                if (currentTask) {
                    this.currentFragments = this.planningStore.getFragmentsByIds(currentTask.fragments ?? []);
                    let dbids = this.currentFragments.map((f: Fragment) => f.dbId).filter((id: number | undefined): id is number => id !== undefined);

                    if (dbids.length > 0) {
                        this.viewer.isolate(dbids);
                        this.viewer.fitToView(dbids);
                    }
                } else {
                    this.setFragColorAtDate(this.getFragAtDate(), true);
                    this.viewer.fitToView();
                    this.currentFragments = [];
                }

                this.viewer.select([]);
            });

            // 📊 Métriques actualisées
            effect(() => {
                this.metrics = this.planningStore.metrics();
            });

            // 🗓️ Colorer par date
            effect(() => {
                this.currentDate = this.planningStore.currentDate();
                const currentTask = this.currentTask();
                this.setFragColorAtDate(this.getFragAtDate(), currentTask ? false : true);
            });

            // 🔒 Isoler fragments
            effect(() => {
                this.selectedFragments = this.planningStore.isolatedFragments();

                if (this.selectedFragments.length > 1) {
                    this.setFragColorAtDate(this.selectedFragments, true);
                    this.viewer.fitToView(this.selectedFragments.map((f: Fragment) => f.dbId).filter((id: number | undefined): id is number => id !== undefined));
                    this.viewer.select([])
                }
            })

            // ✨ Sélection fragments
            effect(() => {
                this.selectedFragments = this.planningStore.selectedFragments();

                if (this.selectedFragments.length > 1) {
                    this.setFragColorAtDate(this.selectedFragments, false);
                    this.viewer.fitToView(this.selectedFragments.map((f: Fragment) => f.dbId).filter((id: number | undefined): id is number => id !== undefined));
                    this.viewer.select([])
                }
            })
        });

        const materials: any = this.viewer.impl.matman();

        materials.addMaterial('todoMaterial', this.todoMaterial, true);
        materials.addMaterial('doneMaterial', this.doneMaterial, true);
        materials.addMaterial('progressMaterial', this.progressMaterial, true);
        materials.addMaterial('lateMaterial', this.lateMaterial, true);
        materials.addMaterial('aheadMaterial', this.aheadMaterial, true);
        materials.addMaterial('unusedMaterial', this.unusedMaterial, true);

        this.viewer.impl.createOverlayScene('fragmentObjects', this.fragmentObjectMaterial as any);
        this.viewer.impl.createOverlayScene('sphereZone', new THREE.MeshBasicMaterial({ color: 0xFF0000 }) as any);
        this.viewer.impl.createOverlayScene('fragmentZone');

        this.viewer.setPropertiesOnSelect(false);

        this.viewer.addEventListener(Autodesk.Viewing.SELECTION_CHANGED_EVENT, (event: any) => {
            if (event.dbIdArray && event.dbIdArray.length > 0) {
                this.getSelection();
            } else {
                this.planningStore.selectFragments([]);
            }
        });

        this.viewer.addEventListener(Autodesk.Viewing.OBJECT_TREE_CREATED_EVENT, async () => {
            this.instanceTree = this.viewer.model?.getData()?.instanceTree;
        })

        this.viewer.addEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, async () => {
            this.instanceTree = this.viewer.model?.getData()?.instanceTree;
            this.setFragColorAtDate(this.getFragAtDate(), true);

            if (this.startDate && this.endDate) {
                this.startDate = subSeconds(new Date(this.startDate), 1).toISOString();
                this.endDate = addSeconds(new Date(this.endDate), 1).toISOString();

                if (this.task) {
                    let taskFragments = this.fragments.filter((f: Fragment) =>
                        this.task.fragments?.includes(f.id) &&
                        f.objects && f.objects.length &&
                        f.objects?.find((o: any) => o.createdDate && isWithinInterval(new Date(o.createdDate), { start: new Date(this.startDate), end: new Date(this.endDate) }))
                    )

                    this.selectFrag(taskFragments.map(f => f.id))
                }
            }

            this.setDayData(this.metrics, this.fragments);
            this.selectedFragments = this.planningStore.selectedFragments();
            this.viewer.setBackgroundColor(255, 255, 255, 255, 255, 255);
            let viewcuiext = this.viewer.getExtension('Autodesk.ViewCubeUi')

            this.viewer.loadExtension('Autodesk.NPR').then(() => {
                let viewNPRext = this.viewer.getExtension('Autodesk.NPR') as any;
                viewNPRext.setParameter("preserveColor", true);

                // // change to Orthographic mode
                setTimeout(() => {
                    (viewcuiext as any).setViewCube('top');
                    this.viewer.navigation.toOrthographic();
                }, 500)
            })
        })


        this.viewer.registerContextMenuCallback('IsolateSame', (menu: any[], status: any) => { // Context menu types not fully available
            this.selectedFragIds = this.viewer.getSelection().map(id => id.toString());

            if (this.viewer.getSelectionCount())
                menu.push({
                    title: this.translate.instant('planning.isolate_same'),
                    target: () => {
                        this.isolateSame(this.selectedFragIds.map(id => parseInt(id, 10)));
                    }
                });
        });

        // this.viewer.registerContextMenuCallback('HideSame', (menu: any[], status: any) => { // Context menu types not fully available
        //     this.selectedFragIds = this.viewer.getSelection().map(id => id.toString());

        //     if (this.viewer.getSelectionCount())
        //         menu.push({
        //             title: this.translate.instant('planning.hide_same_level'),
        //             target: () => {
        //                 this.hideSameLevel(this.selectedFragIds.map(id => parseInt(id, 10)));
        //             }
        //         });
        // });

        this.viewer.registerContextMenuCallback('LinkToTaskMenu', (menu: any[], status: any) => { // Context menu types not fully available
            this.selectedFragIds = this.viewer.getSelection().map(id => id.toString());

            if (this.viewer.getSelectionCount())
                menu.push({
                    title: this.translate.instant('planning.link_task'),
                    icon: 'adsk-icon-link',
                    target: () => {
                        this.linkToTask(this.selectedFragIds.map(id => parseInt(id, 10)));
                    }
                });
        });


        this.viewer.registerContextMenuCallback('SetTaskStatusMenu', (menu: any[], status: any) => {
            this.selectedFragIds = this.viewer.getSelection().map(id => id.toString());

            if (this.viewer.getSelectionCount() == 1)
                menu.push({
                    title: this.translate.instant('planning.select_linked_task'),
                    target: () => {
                        this.selectLinkedTask();
                    }
                });
        });

        this.instanceTree = this.viewer.model?.getData()?.instanceTree;
    }

    initOverlay() {
        try {
            Object.keys(this.colors).forEach((c: string) => {
                let material: any = new THREE.MeshBasicMaterial({
                    color: this.colors[c]
                });

                (this.viewer.impl as any).createOverlayScene(c, material);

                let lineMaterial = new THREE.LineBasicMaterial({
                    color: this.colors[c],
                    transparent: false,
                    linewidth: 5
                });

                (this.viewer.impl as any).createOverlayScene('trace-' + c, lineMaterial);
            });
        } catch (e) {
            console.error(e)
        }
    }

    getSelection() {
        let ids = this.viewer.getSelection();
        let frags: Fragment[] = [];
        let parentFrags: Fragment[] = [];
        let childIds: number[] = [];
        let selectedTasks: PlanningTask[] = [];

        for (let k in this.zoneMeshes) {
            this.zoneMeshes[k]!.visible = false;
        }

        ids.forEach((dbId: number) => {
            let children = this.getLastChildren(dbId);
            childIds = childIds.concat(children);
        });

        this.fragments.forEach(async (f: Fragment, i: number) => {
            if (f.dbId !== undefined && ids.includes(f.dbId)) {
                parentFrags.push(f);
            }

            if (f.volume && f.dbId !== undefined && childIds.includes(f.dbId)) {
                frags.push(f);
                let task = this.getFragmentTask(f.id);

                if (task && !selectedTasks.find((t: PlanningTask) => t.id == task.id)) {
                    selectedTasks.push(task)
                }
            }
        });

        if (frags.length) {
            this.planningStore.selectFragments(frags);
        }

        this.selectedFragIds = frags.map(f => f.id);
        return frags;
    }

    getFragmentTask(id: string): PlanningTask | undefined {
        let task = this.planningStore.getFragmentTask([id])
        return task[0]
    }

    getFragmentMetrics(id: string): Metric[] {
        const task = this.tasks().find(t => t.fragments && t.fragments.indexOf(id) >= 0);
        return task?.metrics || [];
    }

    // getText(text: string): THREE.Mesh {
    //     let size = 0.4;

    //     let geom = new TextGeometry(text, {
    //         font: new Font(FontJson),
    //         size,
    //         depth: 0.05,
    //         curveSegments: 3,
    //     });

    //     geom.computeBoundingBox();
    //     if (geom.boundingBox) {
    //         geom.translate(geom.boundingBox.max.x * -0.5, 0.5, 0);
    //     }

    //     var mat = new THREE.MeshBasicMaterial({ color: "#000000" });

    //     let textMesh = new THREE.Mesh(geom, mat);
    //     if (this.viewer && !this.viewer.model?.getData()?.is2d) {
    //         textMesh.rotateY(90 * Math.PI / 180);
    //         textMesh.rotateZ(90 * Math.PI / 180);
    //     } else {
    //         // textMesh.rotateZ(-1 * this.model.origin.zAngle * Math.PI / 180); // model.origin not available
    //     }
    //     textMesh.updateMatrix();
    //     return textMesh
    // }

    // removeFragmentObjects() {
    //     this.fragmentObjects.map(f => {
    //         this.viewer.impl.removeOverlay('fragmentObjects', f);
    //     });
    //     this.fragmentObjects = [];

    // }
    // async addFragmentObjects(fragment) {
    //     let props: any = await this.getFragProperties(fragment.dbId);
    //     fragment.objects.forEach(obj => {
    //         let metricMesh = new THREE.Mesh(new THREE.SphereGeometry(0.1, 32), new THREE.MeshBasicMaterial({
    //             side: THREE.DoubleSide,
    //             color: '#010101'
    //         }));
    //         if (props && props.center.z) obj.z = props.center.z;
    //         let { x, y, z } = Tools.applyTransform2(obj, this.model.origin, this.model.offset);

    //         metricMesh.position.x = x;
    //         metricMesh.position.y = y;
    //         // if (props && props.center.z) z = props.center.z - this.model.origin.position.z - this.model.offset.z;
    //         metricMesh.position.z = z;
    //         if (props && props.height) metricMesh.position.z += props.height;
    //         metricMesh.updateMatrix();
    //         // metricMesh.add(this.getText(obj.name));
    //         this.viewer.impl.addOverlay('fragmentObjects', metricMesh);
    //         this.fragmentObjects.push(metricMesh)
    //     })

    // }

    getFragmentById(id: string): Fragment | undefined {
        let index = this.fragIndex[id];
        return index !== undefined ? this.fragments[index] : undefined;
    }

    // updateFragment(fragment: Fragment): void {
    //     this.fragments.forEach((f: Fragment) => {
    //         if (f.id == fragment.id) {
    //             f.status = fragment.status;
    //             f.objects = fragment.objects;
    //             f.lastUpdate = fragment.lastUpdate;
    //             f.volumeProgress = fragment.volumeProgress || 0;
    //         }
    //     })

    //     let dockerFrag = this.selectedFragments();

    //     dockerFrag.forEach((f: Fragment) => {
    //         if (f.id == fragment.id) {
    //             f.status = fragment.status;
    //             f.objects = fragment.objects;
    //             f.lastUpdate = fragment.lastUpdate;
    //             f.volumeProgress = fragment.volumeProgress || 0;
    //         }
    //     })

    //     // this.selectedFragments.next(dockerFrag);
    //     // this.mainPanel.updateFragment(fragment);
    // }

    getDbFragments() {
        return this.dbFragments;
    }

    setFragIndex() {
        this.fragIndex = {};
        this.fragNameIndex = {};

        this.fragments.forEach((f: Fragment, i: number) => {
            this.fragIndex[f.id] = i;
            let slice = f.name?.slice(0, 6);

            if (slice && this.fragNameIndex[slice]) {
                this.fragNameIndex[slice].push(f.dbId!)
            } else if (slice) {
                this.fragNameIndex[slice] = [f.dbId!];
            }
        })
    }

    //* fixture *
    getFragmentZones() {
        let fragToUpdate = this.fragments.filter((f: Fragment) => {
            if (!f.polygon?.length || !f.zone || !f.zone.shape.length)
                return false;

            return true;
        });

        return fragToUpdate
    }

    //* fixture *
    updateMissingZone() {
        let fragToUpdate: Fragment[] = [];

        this.fragments.forEach((f: Fragment) => {
            // if (!f.shape || !f.shape.length) {
            let zone = this.getFragZone(f.dbId!);

            if (zone && zone.shape && zone.shape.length) {
                f.zone = zone;
                fragToUpdate.push(f)
            }
            // }
        });

        if (fragToUpdate.length)
            this.planningSub = this.planningService.updateFragments(fragToUpdate.map(f => f as PutFragment)).subscribe({
                next: (result) => {
                    //attributes _id
                },
                error: (error) => {
                    console.error(error);
                    // loader.hide();
                }
            });
    }

    async updateFragmentsDb() {
        try {
            this.fragments = await this.getModelFragments();

            if (this.fragments && this.fragments.length) {
                let fragments = this.fragments.filter(f => f.volume)
                this.planningServiceSub = this.planningService.updateFragments(fragments.map(f => f as PutFragment)).subscribe({
                    next: (result) => {
                        //attributes _id
                    },
                    error: (error) => {
                        console.error(error);
                        // loader.hide();
                    }
                });
            }
        } catch (e) {
            console.error(e)
        }
    }

    async createFragmentsDb() {
        return new Promise(async (resolve, reject) => {
            try {
                this.fragments = await this.getModelFragments();

                if (this.fragments && this.fragments.length) {
                    this.uploadingFragments = true;
                    this.planningAddFragSub = this.planningService.addFragments(this.fragments).subscribe({
                        next: (result) => {
                            //attributes _id
                            this.fragments.forEach((f: Fragment) => {
                                let frag = result.find((r: any) => f.fragId == r.fragId && f.dbId == r.dbId)
                                if (frag) f.id = frag.id;
                            })

                            this.uploadingFragments = false;
                            // loader.hide();
                            resolve(true)
                        },
                        error: (error) => {
                            console.error(error);
                            this.uploadingFragments = false;
                            resolve(true)
                            // loader.hide();
                        }
                    });
                }
            } catch (e) {
                console.error(e)
                resolve(true)
            }
        })
    }

    createFragmentsDbAsync(): Observable<{ status: string, progress: number, total: number }> {
        return new Observable(observer => {
            observer.next({ status: "frag_building", progress: 0, total: 0 });
            let planning = this.planningStore.planning();

            this.getModelFragments().then(fragments => {
                this.fragments = fragments;
                const total = fragments.length;

                this.fragments.forEach((f: Fragment) => {
                    let task = this.getFragmentTask(f.id);
                    if (task) {
                        f.task = task.id;
                    } else if (f.taskNumber) {
                        task = this.tasks().find(t => t.number?.toString() === f.taskNumber?.toString());
                        if (task) f.task = task.id;
                    }
                    f.planning = planning.id;
                });

                if (total === 0) {
                    observer.next({ status: "frag_saved", progress: 0, total: 0 });
                    observer.complete();
                    return;
                }

                observer.next({ status: "frag_building", progress: total, total });

                this.uploadingFragments = true;
                this.planningAddFragSub = this.planningService.addFragments(this.fragments).subscribe({
                    next: (result) => {
                        this.fragments.forEach((f: Fragment) => {
                            const frag = result.find((r: any) => f.fragId == r.fragId && f.dbId == r.dbId);
                            if (frag) f.id = frag.id;
                        });
                        this.uploadingFragments = false;
                        observer.next({ status: "frag_saved", progress: total, total });
                        observer.complete();
                    },
                    error: (error) => {
                        console.error(error);
                        this.uploadingFragments = false;
                        observer.next({ status: "frag_error", progress: 0, total });
                        observer.complete();
                    }
                });
            });
        });
    }

    findFragments(fragments: any[]): any[] {
        return fragments.map((f: any) => {
            // let frag = this.fragments.find(_f => _f.id == f);

            let frag = this.getFragmentById(f)
            if (frag) {
                this.getFragProperties(frag.dbId!)//expect dbId
                return frag.dbId
            }
            return null
        }).filter((f: any) => f)
    }

    findFragmentsByStatus(fragments: any[], status: string): (Fragment | null)[] {
        return fragments.map((f: any) => {
            // let frag = this.fragments.find(_f => _f.id == f);
            let frag = this.getFragmentById(f)
            if (frag && frag.status == status) {
                return frag
            }
            return null
        })
    }

    public selectFragByDbId(fragmentDbIds: number[]) {
        this.viewer.select(fragmentDbIds);
    }

    public selectFrag(fragmentIds: string[]) {
        try {
            let frags = this.findFragments(fragmentIds);
            this.viewer.select(frags);
        } catch (e) {
            console.error(e);
        }
    }

    public getFragAtDate(): Fragment[] {
        if (!this.currentDate) return this.fragments;

        let frags = [];
        let index = 0;

        index = lowerBound(this.fragments, this.currentDate, "aheadDate")
        frags = [...this.fragments].slice(0, index);
        return frags;
    }

    public setFragColorAtDate(fragments: Fragment[], isolate: boolean = false) {
        try {
            const date = this.currentDate ?? new Date().toISOString();
            let progressFrags: number[] = [];

            for (let frag of fragments) {
                let volume = 0;
                let wip = false;
                let material: any = this.unusedMaterial;
                let colorName = "unused";

                if (!this.realtime) {
                    if (frag.aheadDate && frag.dueDate) {
                        if (frag.dueDate && date && isAfter(endOfDay(new Date(date)), new Date(frag.dueDate))) {
                            material = this.doneMaterial;
                            colorName = "done";
                            progressFrags.push(frag.dbId);
                        }

                        if (frag.aheadDate && frag.dueDate && date &&
                            isWithinInterval(endOfDay(new Date(date)), { start: new Date(frag.aheadDate), end: new Date(frag.dueDate) })) {
                            material = this.progressMaterial;
                            colorName = "progress";
                            progressFrags.push(frag.dbId);
                        }
                    }
                } else {
                    let lastUpdate = frag.doneDate || frag.lastUpdate;

                    if (frag.objects && frag.objects.length)
                        frag.objects.forEach((o: any) => {
                            if (o.createdDate && date && isBefore(startOfDay(new Date(o.createdDate)), new Date(date))) {
                                wip = true;

                                if (o.volume) {
                                    volume += o.volume
                                }
                            }
                        })

                    let fragVolume = frag.mapping && frag.mapping.dimension ? frag.mapping.dimension.volume : frag.volume;
                    let progress = fragVolume ? volume / fragVolume : 0;

                    if (!wip || progress == 0) {
                        if (frag.dueDate && date && isAfter(endOfDay(new Date(date)), new Date(frag.dueDate))) {
                            material = this.lateMaterial;
                            colorName = "late";
                        } else {
                            if (frag.aheadDate && date && isAfter(endOfDay(new Date(date)), new Date(frag.aheadDate))) {
                                material = this.todoMaterial;
                                colorName = "todo";
                            }
                        }
                    }

                    if (wip || (progress > 0 && progress <= 0.95)) {
                        material = this.progressMaterial;
                        colorName = "progress";
                    }

                    if ((progress >= 0.95 || frag.status == "done" || frag.doneDate) &&
                        frag.doneDate && date &&
                        isAfter(endOfDay(new Date(date)), new Date(frag.doneDate))) {
                        material = this.doneMaterial;
                        colorName = "done";
                    } else {
                        let task;

                        if (frag.task) {
                            task = this.planningStore.getTaskById(frag.task);
                            if (task && task.doneDate && isAfter(endOfDay(new Date(date)), new Date(task.doneDate))) {
                                material = this.doneMaterial;
                                colorName = "done";

                            }
                        }
                    }

                    const displayModel = this.planningStore.displayModel();
                    const shouldShow = displayModel[colorName as keyof PlanningDisplayModel];

                    if (shouldShow) {
                        progressFrags.push(frag.dbId);
                    }
                }

                const vec4 = this.viewerColors[colorName];

                this.viewer.setThemingColor(
                    frag.dbId,
                    new THREE.Vector4(vec4.x, vec4.y, vec4.z, vec4.w)
                );
            }

            (this.viewer.impl as any).toggleGhosting(false);
            if (isolate) {
                this.viewer.hideAll();
                this.viewer.show(progressFrags);
            }

            this.viewer.impl.invalidate(true);
        } catch (e) {
            console.error(e)
        }
    }

    public setFragColor(fragments: any[], isolate: boolean = false) {
        let doneFrags = this.findFragmentsByStatus(fragments, 'done');
        let progressFrags = this.findFragmentsByStatus(fragments, 'progress');
        let todoFrags = this.findFragmentsByStatus(fragments, 'todo');
        let undefinedFrags = this.findFragmentsByStatus(fragments, 'undefined');
        todoFrags = todoFrags.concat(undefinedFrags)
        const tree = this.viewer.model?.getInstanceTree();
        let fragList: any = this.viewer.model?.getFragmentList();

        if (tree && fragList) {
            doneFrags.forEach((f: Fragment | null) => {
                tree.enumNodeFragments(
                    f?.dbId!,
                    (fragid: number) => fragList.setMaterial(fragid, ViewerMaterials.doneMaterial),
                    true
                );
            })

            progressFrags.forEach((f: Fragment | null) => {
                tree.enumNodeFragments(
                    f?.dbId!,
                    (fragid: number) => fragList.setMaterial(fragid, ViewerMaterials.progressMaterial),
                    true
                );
            })

            todoFrags.forEach((f: Fragment | null) => {
                tree.enumNodeFragments(
                    f?.dbId!,
                    (fragid: number) => fragList.setMaterial(fragid, ViewerMaterials.todoMaterial),
                    true
                );
            })
        }

        if (isolate) {
            const allFrags = todoFrags.concat(progressFrags).concat(doneFrags)
                .filter((f): f is Fragment => f !== null)
                .map(f => f.dbId)
                .filter((id): id is number => id !== undefined);
            this.viewer.isolate(allFrags);
        }

        this.viewer.impl.invalidate(true, true, true)
    }

    public isolateFrags(fragments: any[]) {
        let frags = this.findFragments(fragments);
        this.viewer.isolate(frags);
    }

    selectLinkedTask(): void {
        // let frag = this.fragments.find(f => f.dbId == dbId)
        let frags = this.getSelection()

        if (frags && frags[0]) {
            let task = this.planningStore.getFragmentTask([frags[0].id])
            this.planningStore.selectTasks(task);
        }
    }

    hideSameLevel(ids: number[]): void {
        let selections = this.viewer.getSelection();
        let frags: number[] = [];
        let selectedFrags: Fragment[] = this.fragments.filter(f => selections.includes(Number(f.dbId)));
        let names: string[] = [];
        let fragsToHide: number[] = [];

        selectedFrags.forEach((s: Fragment) => {
            fragsToHide = fragsToHide.concat(this.fragments.filter((f: Fragment) => f.level == s.level).map((f: Fragment) => f.dbId!))
        })

        if (fragsToHide.length)
            this.viewer.hide(fragsToHide)
    }

    isolateSame(ids: number[]): void {
        let selections = this.viewer.getSelection();
        let frags: number[] = [];
        let selectedFrags: Fragment[] = this.fragments.filter(f => selections.includes(Number(f.dbId)));
        let names: string[] = [];

        selectedFrags.forEach((s: Fragment) => {
            let parentFrag: any = this.fragIndex[s.parent!];

            if (names.includes(s.name!)) return;

            names.push(s.name!)
            let fragIndexes = this.fragNameIndex[s.name!.slice(0, 6)];

            if (fragIndexes && fragIndexes.length) {
                frags = frags.concat(fragIndexes)
            }

            if (parentFrag) {
                if (names.includes(parentFrag.name)) return;

                names.push(parentFrag.name)
                fragIndexes = this.fragNameIndex[s.name!.slice(0, 6)];

                if (fragIndexes && fragIndexes.length) {
                    frags = frags.concat(fragIndexes)
                }
            }
        })

        if (frags.length)
            this.viewer.isolate(frags)
    }

    linkToTask(ids: number[]): void {
        if (!this.currentTask()) {
            console.warn('Aucune tâche sélectionnée');
            return;
        }

        if (ids.length === 0) {
            console.warn('⚠️ Aucun fragment sélectionné en 3D');
            return;
        }

        const selectedFragments = this.fragments.filter((f: Fragment) =>
            ids.includes(f.dbId!)
        );

        if (selectedFragments.length === 0) {
            console.warn('⚠️ Aucun fragment trouvé pour les dbIds:', ids);
            return;
        }

        if (this.linkFragsToTask) {
            this.linkFragsToTask(selectedFragments, this.currentTask()!);
        }
    }

    linkMetric(ids: number[]): void {
        let frags: Fragment[] = []
        let childIds: number[] = []

        ids.forEach((dbId: number) => {
            let children = this.getLastChildren(dbId)
            childIds = childIds.concat(children)
        })

        this.fragments.forEach((f: Fragment) => {
            if (childIds.indexOf(f.dbId!) >= 0) {
                frags.push(f);
            }
        })

        this.onLinkMetric(frags[0])
    }

    getLastChildren(id: number): number[] {
        let children: number[] = [];

        const findChild = (current: number) => {
            let currentChildren: (number | undefined)[] = []

            this.fragments.forEach((f: Fragment) => {
                if (f.parent == current) currentChildren.push(f.dbId);
            })

            if (currentChildren.length) {
                currentChildren.forEach((cf: number | undefined) => {
                    if (cf !== undefined) findChild(cf)
                })
            } else {
                children.push(current)
            }
        }

        findChild(id)
        return children;
    }

    async getModelFragments(): Promise<Fragment[]> {
        try {
            var cbCount = 0;
            var fragments: Fragment[] = [];
            var parents: number[] = [];
            var tree: any;

            this.instanceTree = this.viewer.model?.getData()?.instanceTree;
            const getLeafComponentsRec = async (current: number, parent: number) => {
                try {
                    cbCount++;

                    let childCount = tree.getChildCount(current);
                    if (childCount != 0) {
                        await new Promise(resolve => {
                            tree.enumNodeChildren(current, function (children: number) {
                                childCount--;
                                getLeafComponentsRec(children, current).then(() => {
                                    if (childCount == 0) resolve(true);
                                });
                            }, false);
                        })
                    }

                    // var nodeName = this.viewer.model.getInstanceTree().getNodeName(current);
                    if (parents.indexOf(parent) == -1) parents.push(parent);
                    let properties: any = await this.getFragProperties(current, true);//expect dbId
                    if (properties.volume)
                        fragments.push({ parent, ...properties, planning: this.planning, model: this.model?.id })

                } catch (e) {
                    console.error(e)
                }
            }

            tree = await this.getObjectTree();

            var rootDbId = tree.getRootId();
            // var nodeName = this.viewer.model.getInstanceTree().getNodeName(rootId)
            // fragments.push({ id: rootId, parent: '#', text: nodeName })
            await getLeafComponentsRec(rootDbId, 0);

            return fragments;

        } catch (e) {
            console.error(e)
            return [];
        }
    }

    getObjectTree(): Promise<any> {
        return new Promise((resolve, reject) => {
            this.viewer.getObjectTree((objectTree: any) => {
                resolve(objectTree)

            });
        })
    }
    getTransformedPoints(polygon: number[][]): XYZ[] {
        let pointTransformed = [];
        for (var i = 0; i < polygon.length; i++) {
            if (!polygon[i] || !Array.isArray(polygon[i]) || polygon[i]!.length < 2) continue;
            let point = Tools.reverseTransform2({ x: polygon[i]![0]!, y: polygon[i]![1]!, z: 0 },
                this.model?.origin || {} as any,
                this.model?.offset || {} as any);

            pointTransformed.push([
                point.x,
                point.y
            ]);
        }

        return pointTransformed.map(p => ({ x: p[0], y: p[1], z: 0 } as XYZ));
    }

    extendShape(vertices: XYZ[], dist: number = 1): { length: number, points: number[][] } {

        let points = [];
        let length = 0;

        for (let i = 0; i < vertices.length; i++) {
            let A = vertices[i]
            let n = i + 1;
            if (n == vertices.length) n = 0;
            let B = vertices[n];
            n = i + 2;
            if (n == vertices.length) n = 0;
            if (n > vertices.length) n = 1;
            let C = vertices[n];

            if (!A || !B || !C) continue;
            const lineCD = Tools.paralleleLine(A as any, B as any, dist);
            const lineEF = Tools.paralleleLine(B as any, C as any, dist);
            const intersectionPoint = Tools.findIntersection(lineCD, lineEF, 2, dist * 3);

            points.push(intersectionPoint);
            if (points.length > 1) {
                length += Tools.distance(points[points.length - 2]!, points[points.length - 1]!)
                // addLength(points[points.length - 2], points[points.length - 1])
            }
        }
        if (points.length > 1) {
            length += Tools.distance(points[0]!, points[points.length - 1]!)
            // addLength(points[0], points[points.length - 1]);
        }
        return { length, points: points.map(p => p && typeof p === 'object' && 'x' in p && 'y' in p ? [p.x as number, p.y as number] : [0, 0]) };
    }

    calculateZone(geometry: any, dist: number = 1) {
        const THREE: any = (window as any).THREE;
        try {

            // let faces = Tools.getFacesFromGeometry(geometry);
            // let vertices = Tools.getVerticesFromGeometry(geometry);

            let faces = geometry.faces;
            let vertices = geometry.vertices;

            let edges: number[][] = [];
            let arrangedEdges: number[][] = [];
            let n = 0;

            var shapes: THREE.Shape[] = [];
            var outlineShapes: THREE.Shape[] = [];
            var lines: number[][][] = [];
            var openIndex = 0;
            let lineIndex = -1;

            //get edges
            for (let i = 0; i < faces.length; i++) {
                let A = [faces[i]!.a, faces[i]!.b];
                let B = [faces[i]!.b, faces[i]!.c];
                let C = [faces[i]!.c, faces[i]!.a];
                let currentEdge = [A, B, C];

                for (let j = 0; j < 3; j++) {
                    let E = currentEdge[j];
                    if (E && E.length >= 2) {
                        let I = edges.findIndex(e => (e[0] == E[0] && e[1] == E[1]) || (e[0] == E[1] && e[1] == E[0]));
                        if (I == -1) {
                            edges.push(E);
                        } else {
                            edges.splice(I, 1)
                        }
                    }
                }
            }

            if (edges.length < 3) return null;

            //arrange Edges 
            let edge = edges[0];
            if (!edge) return null;
            edges.shift();
            arrangedEdges.push(edge);
            while (edges.length) {
                if (!edge) break;
                n = edges.findIndex(e => edge && e[0] == edge[1]);
                if (n >= 0) {
                    edge = edges[n];
                    if (edge) {
                        arrangedEdges.push(edge);
                        edges.splice(n, 1);
                    }
                } else {
                    n = edges.findIndex(e => edge && e[1] == edge[1]);
                    if (n >= 0) {
                        const foundEdge = edges[n];
                        if (foundEdge && foundEdge[1] !== undefined && foundEdge[0] !== undefined) {
                            edge = [foundEdge[1], foundEdge[0]];
                            arrangedEdges.push(edge);
                            edges.splice(n, 1);
                        }
                    } else {
                        edge = edges[0];
                        if (edge) {
                            arrangedEdges.push(edge);
                            edges.shift();
                        }
                    }
                }
            }
            edges = arrangedEdges;

            //get vertice order
            let order: number[] = [];
            edges.forEach((e: number[]) => {
                if (e[0] == undefined) return;
                order.push(e[0]!)
                if (order.indexOf(e[1]!) >= 0) {
                    order.push(e[1]!);
                }
            });

            if (edges.length < 3) return null;

            //build shape
            order.forEach((o: number, i: number) => {
                const vertex = vertices[o!];
                if (!vertex) return;

                if (openIndex >= 0) {
                    lines.push([[vertex.x, vertex.y]]);
                    lineIndex++;
                    openIndex = -1;
                } else {
                    if (lines[lineIndex]) {
                        lines[lineIndex]!.push([vertex.x, vertex.y]);
                    }
                }
                openIndex = order.findIndex((_o, _i) => _o == o && _i < i);
            })

            lines.forEach((line: number[][], _i: number) => {

                let lengthO1 = 0;
                let lengthO2 = 0;
                line.pop();
                if (line.length < 3) return;
                let shape = new THREE.Shape();
                let shapeO1 = new THREE.Shape();
                let shapeO2 = new THREE.Shape();
                line.forEach((v: number[], i: number) => {
                    if (!v || v.length < 2) return;

                    if (i == 0) {
                        shape.moveTo(v[0]!, v[1]!);
                    } else {
                        shape.lineTo(v[0]!, v[1]!);
                    }

                    if (i == line.length - 1 && line[0] && line[0].length >= 2) {
                        shape.lineTo(line[0][0]!, line[0][1]!);
                    }
                })

                let outline1 = this.extendShape(line.map(p => ({ x: p[0]!, y: p[1]!, z: 0 })), dist)
                if (outline1 && outline1.points && outline1.points.length >= 3) {
                    outline1.points.forEach((v: number[], i: number) => {
                        if (!v || v.length < 2) return;

                        if (i == 0) {
                            shapeO1.moveTo(v[0]!, v[1]!);
                        } else {
                            shapeO1.lineTo(v[0]!, v[1]!);
                        }
                        if (i == outline1.points.length - 1 && outline1.points[0] && outline1.points[0].length >= 2) {
                            // lengthO1 += Tools.distance(outline1.points[0], v);
                            shapeO1.lineTo(outline1.points[0][0]!, outline1.points[0][1]!);
                        }
                    })
                }

                let outline2 = this.extendShape(line.reverse().map(p => ({ x: p[0]!, y: p[1]!, z: 0 })), dist)
                if (outline2 && outline2.points && outline2.points.length >= 2) {
                    outline2.points.forEach((v: number[], i: number) => {
                        if (!v || v.length < 2) return;

                        if (i == 0) {
                            shapeO2.moveTo(v[0]!, v[1]!);
                        } else {
                            // lengthO2 += Tools.distance(outline2.points[i - 1], v);
                            shapeO2.lineTo(v[0]!, v[1]!);
                        }
                        if (i == outline2.points.length - 1 && outline2.points[0] && outline2.points[0].length >= 2) {
                            // lengthO2 += Tools.distance(outline2.points[0], v);
                            shapeO2.lineTo(outline2.points[0][0]!, outline2.points[0][1]!);
                        }
                    })
                }
                shapes.push(shape)
                outlineShapes.push(outline1?.length > outline2?.length ? shapeO1 : shapeO2)
            })

            if (!shapes.length) return null;
            return {
                lines,
                shapes,
                outlineShapes,
                geometry: new THREE.ShapeGeometry(shapes),
                outlineGeometry: new THREE.ShapeGeometry(outlineShapes)
            }
        } catch (e) {
            console.error(e);
            return null
        }
    }


    getFragZone(dbId: number): ZoneShape {
        let zone;
        let fragIds: number[] = [];

        this.instanceTree!.enumNodeFragments(dbId, (fragId) => {
            fragIds.push(fragId)
        })

        let melteGeometry = new THREE.BufferGeometry();
        let zoneGeometry: any;
        // to change material or transform, need to iterate all
        // fragments of a given dbId and apply same material/transform 

        let maxZ = -Infinity;
        let maxLocalZ = -Infinity;
        let minZ = Infinity;
        let minLocalZ = Infinity;
        let shape: number[][][] = [];
        fragIds.forEach((fragId: number, i: number) => {

            var renderProxy = this.viewer.impl.getRenderProxy(this.viewer.model, fragId);
            var fragProxy = this.viewer.impl.getFragmentProxy(this.viewer.model, fragId);
            let matrix = new THREE.Matrix4();
            fragProxy.getAnimTransform();
            fragProxy.getWorldMatrix(matrix);

            let geom = new THREE.BufferGeometry() as any;
            let VE = Autodesk.Viewing.Private.VertexEnumerator;
            let vertices: THREE.Vector3[] = [];
            let faces: { a: number, b: number, c: number, normal?: THREE.Vector3 }[] = [];
            let indices = [];
            if (!renderProxy.geometry) return;
            renderProxy.geometry.computeBoundingBox();
            VE.enumMeshVertices(renderProxy.geometry, (v: any, i: any) => {

                vertices.push(new THREE.Vector3(v.x, v.y, v.z))
                maxZ = Math.max(maxZ, v.z + matrix.elements[14])
                minZ = Math.min(minZ, v.z + matrix.elements[14])
                maxLocalZ = Math.max(maxLocalZ, v.z)
                minLocalZ = Math.min(minLocalZ, v.z)
            });
            VE.enumMeshIndices(renderProxy.geometry, (a: any, b: any, c: any) => {
                indices.push(a)
                indices.push(b)
                indices.push(c)

                if (isNaN(a) || isNaN(b) || isNaN(c)) geom.name = "invalid";
                // faces.push(new THREE.Face3(a, b, c)); // Face3 deprecated
            });
            //crush to top
            // geom.vertices = vertices.map(v => new THREE.Vector3(v.x, v.y, v.z));
            if (geom.name == "invalid" || !vertices || !vertices.length) return;
            let filter = Tools.filterVerticesByZ(faces, vertices, (maxLocalZ + minLocalZ) / 2)
            vertices = filter.vertices;
            faces = filter.faces;
            let merge = Tools.mergeVertices(faces, vertices, 1);
            geom.faces = merge.faces;
            geom.vertices = merge.vertices.map((v: THREE.Vector3) => new THREE.Vector3(v.x, v.y, 0));

            if (geom.computeFaceNormals) geom.computeFaceNormals();
            zone = this.calculateZone(geom, this.model?.origin?.scale || 1);

            if (zone && zone.geometry) {
                zone.lines = zone.lines.map((line: any) => {
                    return line.map((vec: any) => {
                        let vec3 = new THREE.Vector3(vec[0], vec[1], 0)
                        vec3 = vec3.applyMatrix4(matrix);
                        return [vec3.x, vec3.y];
                    })
                })
                shape = shape.concat(zone.lines)
                if (!zoneGeometry) {
                    zoneGeometry = zone.outlineGeometry;
                    (zoneGeometry as any).applyMatrix4?.(matrix)
                } else {
                    (zoneGeometry as any).merge?.(zone.outlineGeometry, matrix);
                }
                (melteGeometry as any).merge?.(geom, matrix);
                zoneGeometry.computeBoundingBox();
                melteGeometry.computeBoundingBox();
            }
        })

        //--> for debbugging purpose
        // geometry.vertices.forEach((v, i) => {
        //     v.z = maxZ;
        //     let mesh = new THREE.Mesh(new THREE.SphereGeometry(0.2, 8, 8), new THREE.MeshBasicMaterial({ color: "#123456" }))
        //     mesh.position.copy(v);
        //     // mesh.position.z = maxZ;
        //     mesh.updateMatrix();
        //     this.viewer.impl.addOverlay('sphereZone', mesh);
        // })
        // geometry.faces.forEach(f => {
        //     this.addLine(geometry.vertices[f.a], geometry.vertices[f.b])
        //     this.addLine(geometry.vertices[f.b], geometry.vertices[f.c])
        //     this.addLine(geometry.vertices[f.c], geometry.vertices[f.a])
        // })
        //-->
        let meltedMesh = new THREE.Mesh(melteGeometry, new THREE.MeshBasicMaterial({ color: "#123456" }))
        meltedMesh.position.z = minLocalZ;
        meltedMesh.updateMatrix();

        // this.viewer.impl.addOverlay('fragmentObjects', meltedMesh);
        let zoneMesh = new THREE.Mesh(zoneGeometry, new THREE.MeshBasicMaterial({ color: "#123456" }))
        zoneMesh.position.z = minLocalZ;
        zoneMesh.visible = false;
        zoneMesh.updateMatrix();

        // this.viewer.impl.addOverlay('fragmentZone', zoneMesh);
        // this.forgeTools.addObject('fragmentZone', zoneMesh)
        this.zoneMeshes[dbId] = zoneMesh
        return new ZoneShape({
            viewer: this.viewer,
            model: this.model!,
            polygon: shape && shape.length > 0 ? shape[0] || [] : [],
            type: 'geometry',
            z: minLocalZ,
            height: 1,
            name: `Fragment-${dbId}`,
            number: 0,
            color: 'primary',
            alpha: 0.5,
            id: `fragment-zone-${dbId}`,
            visible: false,
            geometry: {
                position: (melteGeometry as any).vertices || [],
                normal: []
            }
        })
    }

    mapModelProperties(properties: Autodesk.Viewing.PropertySet): Record<string, any> {
        const categories = ["element", "dimension", "concrete", "location", "planning"];
        const mapping: Record<string, Record<string, any>> = {};
        const props: Record<string, any> = {};

        properties.forEach((p: any) => {
            props[p.displayName.toLowerCase()] = p.displayValue;
        });

        categories.forEach((category: string) => {
            const modelMapping = this.model?.mapping || {};
            const categoryMapping = modelMapping[category] || {};

            Object.keys(categoryMapping).forEach((key: string) => {
                const mappingKey = categoryMapping[key];
                if (mappingKey && props[mappingKey]) {
                    if (!mapping[category]) {
                        mapping[category] = {};
                    }
                    mapping[category][key] = props[mappingKey];
                }
            });
        });

        return mapping;
    }

    getFragProperties(dbId: number, addZone: boolean = false): Promise<any> {
        return new Promise((resolve, reject) => {

            // let childIds = this.getLastChildren(dbId);
            // if(!handledChildren.includes())

            this.viewer.model.getProperties(dbId, (objProp: any) => {//expect dbId

                let externalId = objProp.externalId;

                let name = objProp.name;
                let volume = 0;
                let zone;
                try {
                    var box = new Array();
                    this.instanceTree!.getNodeBox(dbId, box as any)//An array holding 6 number values: (min-x, min-y, min-z, max-x, max-y, max-z)

                    // if (addZone)
                    zone = this.getFragZone(dbId);
                    let currentBBox = new THREE.Box3(new THREE.Vector3(box[0], box[1], box[2]), new THREE.Vector3(box[3], box[4], box[5]))

                    let model = this.buildZoneFromBoudingBox(currentBBox);
                    let mapping: any = this.mapModelProperties(objProp.properties);
                    if (!mapping.dimension || !mapping.dimension.volume) {
                        let scale = 1 / (this.model?.origin?.scale || 1);
                        let calcVolume = Tools.getVolume(this.viewer, dbId, scale);
                        if (calcVolume && !isNaN(calcVolume)) {
                            volume += calcVolume;
                        }
                    } else {
                        volume += mapping.dimension.volume || 0;
                    }
                    const center = new THREE.Vector3();
                    currentBBox.getCenter(center);
                    const { lat, lon } = Tools.reverseTransform2({ x: center.x, y: center.y, z: 0 },
                        this.model?.origin || {} as any,
                        this.model?.offset || {} as any);

                    const fragmentProps = {
                        ...model,
                        shape: zone ? zone.shape : [],
                        zone,
                        x: center.x,
                        y: center.y,
                        lon,
                        lat,
                        volume,
                        name,
                        externalId,
                        mapping,
                        taskNumber: mapping.planning?.taskId,
                        dbId
                    };
                    resolve(fragmentProps)
                } catch (e) {
                    console.error(e)
                    const basicProps = {
                        polygon: [],
                        center: { x: 0, y: 0, z: 0 },
                        height: 0,
                        z: 0,
                        shape: [],
                        volume,
                        name,
                        externalId,
                        mapping: {},
                        dbId
                    };
                    reject(basicProps)
                }

            })
        });
    }
    addLine(prev: XYZ, current: XYZ): THREE.Mesh {
        const THREE: any = (window as any).THREE;
        // const prev = points[points.length - 2];
        //     const current = points[points.length - 1];

        const width = 0.1;
        const height = 0.1;
        const distance = Math.sqrt(
            Math.pow(current.x - prev.x, 2) +
            Math.pow(current.y - prev.y, 2) +
            Math.pow(current.z - prev.z, 2)
        );

        let cubeGeometry = new THREE.BoxGeometry(width, height, distance); // align length with z-axis
        cubeGeometry.applyMatrix4(new THREE.Matrix4().makeTranslation(0, 0, distance / 2)); // so one end is at the origin

        let cube = new THREE.Mesh(cubeGeometry, new THREE.MeshBasicMaterial());
        cube.position.set(prev.x, prev.y, prev.z);
        cube.lookAt(new THREE.Vector3(current.x, current.y, current.z));
        this.viewer.impl.addOverlay(
            "sphereZone", cube);
        return cube;
    }
    buildZoneFromBoudingBox(box: THREE.Box3) {
        let max = Tools.reverseTransform2({ x: box.max.x, y: box.max.y, z: box.max.z }, this.model?.origin || {} as any, this.model?.offset || {} as any)
        let min = Tools.reverseTransform2({ x: box.min.x, y: box.min.y, z: box.min.z }, this.model?.origin || {} as any, this.model?.offset || {} as any)
        // let max = box.max;
        // let min = box.min;
        let points = [
            [box.max.x, box.max.y],
            [box.max.x, box.min.y],
            [box.min.x, box.min.y],
            [box.min.x, box.max.y]
        ]
        let polygon = points;

        let center = {
            x: ((max.x - min.x) * .5) + min.x,
            y: ((max.y - min.y) * .5) + min.y,
            z: ((max.z - min.z) * .5) + min.z,
        }
        let height = max.z - min.z;
        let z = min.z;
        // zone.mesh.position.
        return { polygon, center, height, z };
    }
    public override activate() {
        return true;
    }

    public override deactivate() {
        return true;
    }

    public override load(): any {
        this.viewer.addEventListener(Autodesk.Viewing.TOOLBAR_CREATED_EVENT, this.toolbarCreatedHandler)
        return true;
    }

    public override unload(): any {
        this.fragments = [];
        this.dbFragments = [];
        this.selectedFragIds = [];
        this.viewer.removeEventListener(Autodesk.Viewing.TOOLBAR_CREATED_EVENT, this.toolbarCreatedHandler);
        this.viewer.toolbar.removeControl(this.toolName);

        if (this.planningServiceSub) this.planningServiceSub.unsubscribe();
        if (this.planningAddFragSub) this.planningAddFragSub.unsubscribe();
        if (this.planningSub) this.planningSub.unsubscribe();
        if (this.selectFragSub) this.selectFragSub.unsubscribe();

        return true;
    }

    public createUI() {
        // Toolbar button
        let button = new Autodesk.Viewing.UI.Button('planning-btn');
        this.planningButton = button;

        button.addClass('planning-btn');
        button.setToolTip(this.translate.instant('planning.planning_tool'));
        button.setIcon('planning-btn-icon');

        button.onMouseOver = () => {
            button.setIcon('hover-planning-btn-icon');
        }

        button.onMouseOut = () => {
            button.setIcon('planning-btn-icon');
        }

        button.onClick = () => {
            this.onToolbarButtonClicked();
            this.updateButtonActiveState();
        };

        this.updateButtonActiveState();

        // SubToolbar
        this.subToolbar = new Autodesk.Viewing.UI.ControlGroup('planning-toolbar');
        this.subToolbar!.addControl(button);
        this.viewer.toolbar.addControl(this.subToolbar!);

        const styleId = 'planning-extension-style';
        if (!document.getElementById(styleId)) {
            const link = document.createElement('link');
            link.id = styleId;
            link.rel = 'stylesheet';
            link.href = 'viewer/planning/planning-extension/planning-extension.css';
            document.head.appendChild(link);
        }
    }

    public loadPlanningDockingPanel() {
        if (this.mainPanel !== null) {
            (this.mainPanel as any).mainContainer.innerHTML = '';
            if (this.mainPanel && typeof (this.mainPanel as any).uninitialize === 'function') {
                (this.mainPanel as any).uninitialize();
            }
            this.mainPanel = null;
        }

        this.mainPanel = new PlanningDockingPanel(this.viewer, this.viewer.container, 'planningDockingPanel', 'planning', {
            translate: this.translate,
            model: this.model,
            ganttManager: this.ganttManager,
            planningService: this.planningService,
            planningStore: this.planningStore,
            injector: this.injector,
            onImageClick: this.onImageClick,
            onTaskUpdate: this.onTaskUpdate || null,
            reload: () => this.loadPlanningDockingPanel()
        }) as any;

        // Wait for the DOM are ready
        setTimeout(() => {
            const titleDiv = (this.mainPanel as any)?.container?.querySelector('.docking-panel-title');

            if (titleDiv && !titleDiv.textContent?.trim()) {
                titleDiv.textContent = this.translate.instant('planning.planning_tool') || 'Planning';
            }
        }, 0);

        (this.mainPanel as any).addVisibilityListener?.((visible: boolean) => {
            if (!visible) {
                this.mainPanel = null;
            }

            this.updateButtonActiveState();
        });
    }

    onToolbarButtonClicked() {
        if (this.mainPanel != null) {
            if (this.mainPanel && typeof (this.mainPanel as any).uninitialize === 'function') {
                (this.mainPanel as any).uninitialize();
            }
            this.mainPanel = null;
        } else {
            this.loadPlanningDockingPanel();
        }

        this.updateButtonActiveState();
    }


    // event callbacks
    public onToolbarCreated() {
        this.createUI();
    };

    setDayData(metrics: Metric[], fragments: Fragment[]): void {
        let dayCount = differenceInDays(new Date(this.endDate), new Date(this.startDate));
        for (let i = 0; i <= dayCount; i++) {
            let dayDate = endOfDay(addDays(new Date(this.startDate), i));
            this.days.push({
                date: dayDate.toISOString(),
                name: format(dayDate, 'EEEE dd MMMM'),
                metrics: [],
                fragments: []
            })
        }

        metrics.forEach((m: Metric) => {
            let date = m.payload.startDate ? endOfDay(new Date(m.payload.startDate)).toISOString() : null;
            let day = this.days.find(d => d.date == date);
            if (day) {
                (m as any).color = (day as any).color;
                day.metrics.push(m);
            }
        })

        fragments.forEach((f: Fragment) => {
            f.objects!.forEach((o: any) => {

                let date = o.createdDate ? endOfDay(new Date(o.createdDate)).toISOString() : null;
                let day = this.days.find(d => d.date == date);
                if (day) {
                    day.fragments.push(f);
                }
            })
        })
        this.days.forEach((d: any) => {
            d.volume = 0;
            d.concreting = 0;
            d.concretingVolume = 0;
            d.linear = 0;
            d.element = 0;
            d.surface = 0;
            d.craneCycle = 0;
            d.buildingTypeArray = [];
            d.buildingTypeValues = {};
            d.buildingTypes = {
                "wall": { unit: "m", variable: "linearProgress", volume: 0, value: 0 },
                "prefawall": { unit: "m", variable: "linearProgress", volume: 0, value: 0 },
                "floor": { unit: "m2", variable: "surfaceProgress", volume: 0, value: 0 },
                "prefafloor": { unit: "m2", variable: "surfaceProgress", volume: 0, value: 0 },
                "beam": { unit: "", variable: "", volume: 0, value: 0 },
                "prefabeam": { unit: "", variable: "", volume: 0, value: 0 },
                "column": { unit: "", variable: "", volume: 0, value: 0 },
                "prefacolumn": { unit: "", variable: "", volume: 0, value: 0 },
            };
            d.metrics.forEach((m: Metric) => {
                const payload = m.payload as Record<string, any>;
                const buildingTypeName = payload['buildingType'] as string;
                const buildingType = d.buildingTypes[buildingTypeName];
                const volume = payload['volume'] ? parseFloat(payload['volume']) : parseFloat(payload['weight']) / 2300;
                if (payload['prediction'] && payload['prediction'].includes("Coulage")) {
                    d.concretingVolume += volume;
                    d.concreting++;
                    if (buildingType)
                        d.buildingTypes[buildingTypeName].volume += volume;
                }
                let value;
                if (buildingType && buildingType.variable) {
                    const value = payload[buildingType.variable];
                    d.buildingTypes[buildingTypeName].value += parseFloat(value || 0);
                } else if (buildingType) {
                    d.buildingTypes[buildingTypeName].value++;
                }
                d.volume += volume;


            })
            d.volume = d.volume.toFixed(2);
            d.linear = d.linear.toFixed(2);
            d.surface = d.surface.toFixed(2);
            d.concretingVolume = d.concretingVolume.toFixed(2);
            d.buildingTypeArray = Object.keys(d.buildingTypes).map(k => {
                return {
                    name: k,
                    unit: d.buildingTypes[k].unit,
                    volume: d.buildingTypes[k].volume.toFixed(2),
                    value: d.buildingTypes[k].value,
                    valueText: d.buildingTypes[k].value.toFixed(2)
                }
            }).filter((b: any) => b.value > 0)
        })

    }

    animate() {
        if (!this.viewer || !this.viewer.impl) return;
        const camera: any = this.viewer.getCamera();

        this.fragmentObjects.forEach((frag: THREE.Object3D) => {

            let dist = frag.position.distanceTo(camera.position);
            let scale = 1;
            if (!this.is2d) {

                scale = dist / this.scaleDivider;

                if (this.model && scale < this.model.minUnitScale) return;

                let vector, angle;

                //get the normalized vector of the target
                let target = new THREE.Vector3(camera.position.x, camera.position.y, camera.position.z);
                vector = target.sub(frag.position);
                angle = Math.atan2(vector.y, vector.x);

                if (frag.children[0])
                    frag.children[0].rotation["y"] = angle + 1.57;

            } else {

                scale = camera.position.z / this.scaleDivider;
                // if (scale < this.model.minUnitScale) return;
            }
            // frag.rotation["z"] = angle;
            // frag.lookAt(new THREE.Vector3(camera.position.x, camera.position.y, 0));
            frag.scale.set(scale, scale, scale);
            frag.updateMatrix();
        })

        requestAnimationFrame(() => this.animate());
    }

    private updateButtonActiveState() {
        const active = !!this.mainPanel;

        if (this.planningButton) {
            this.planningButton.setState(
                active
                    ? Autodesk.Viewing.UI.Button.State.ACTIVE
                    : Autodesk.Viewing.UI.Button.State.INACTIVE
            );
        }

        const buttonElement = document.getElementById('planning-btn');
        if (buttonElement) {
            buttonElement.classList.toggle('active', active);
            buttonElement.classList.toggle('inactive', !active);
        }
    }
}